/* ---------------------------------------------------------------------------
   Bridge Rating, mounted inside the one page.

   Bridge Rating was its own document with its own entry point, and it keeps its
   own store: a rating is a long form over an engine version, and its state has
   nothing to share with the register's but the sign-in. What it no longer has
   is its own chrome. The page's title bar and ribbon are the frame for all four
   applications, and the Rating tab of that ribbon is where its four screens are
   reached (src/components/ribbon.ts) — so this module draws only the screen.

   HOW IT SITS IN SOMEBODY ELSE'S RENDER. The main shell draws a slot marked
   `data-keep` where the screen goes (see KEEP in src/core/dom.ts), which the
   main reconciler leaves the contents of alone. This module renders into that
   slot from its own store's subscription — the same arrangement the Gantt and
   the map scenes use. Started the first time the slot appears and not before:
   a session that never opens Bridge Rating never asks /api for a rating.

   Its stylesheet, rating.css, is imported by src/main.ts with the others: this
   module is also imported by the ribbon, which the checks load under Node.
   --------------------------------------------------------------------------- */

import { h, render } from '../core/dom.ts';
import { deploymentBanner } from './chrome.ts';
import { structuresScreen } from './screens/structures.ts';
import { rateInputs, rateScreen, outstandingFor } from './screens/rate.ts';
import { ratingsScreen, structureRatings } from './screens/ratings.ts';
import { methodScreen } from './screens/method.ts';
import { acceptedWatchers, actions } from './actions.ts';
import { store, type Screen, type State } from './store.ts';
import type { Bridge, RegisterDefect } from '../data/types.ts';
import type { RatingRecord } from './api.ts';
import { reratingOf, type RerateStatus } from '../data/rerating.ts';
import { active } from '../tenant/registry.ts';
/* TEMPORARY — the FWO demonstration. Delete with src/data/fwo-demo.ts. */
import { demoRatingsFor } from '../data/fwo-demo.ts';

/**
 * THE SLOTS BRIDGE RATING PAINTS INTO. Its screen goes in the page's content
 * panel; the rating form and this structure's ratings go in the property
 * panel, as pages the explorer picks — see src/screens/detail.ts. Each is a
 * `data-keep` box the main render leaves alone, painted from this store.
 */
export type RatingSlot = 'rating' | 'rating-form' | 'rating-history';

const SLOTS: readonly RatingSlot[] = ['rating', 'rating-form', 'rating-history'];

const SCREENS: Record<Screen, (s: Readonly<State>) => HTMLElement> = {
  structures: structuresScreen,
  rate: rateScreen,
  ratings: ratingsScreen,
  method: methodScreen,
};

/** A box the main shell draws for Bridge Rating to paint into. */
export function ratingSlot(slot: RatingSlot = 'rating'): HTMLElement {
  return h('div', { class: ['rt-host', `rt-host--${slot}`], 'data-keep': slot });
}

/** The box each slot is painted into, as the last sync found it. */
const hosts = new Map<RatingSlot, HTMLElement>();
let started = false;
/** The structure in hand on the page — what the property panel's pages are of. */
let inHand: string | null = null;
const watchers = new Set<() => void>();
const selectWatchers = new Set<(id: string) => void>();
/** The structure Bridge Rating last had selected, to tell a change from a repaint. */
let lastSelected: string | null = null;
/** A rating begun before the register was read, to begin again once it is. */
let pendingRate: string | null = null;

/**
 * Put the caret back where a field was replaced rather than patched — the
 * reconciler keeps the node being typed into in almost every case, but not
 * where the row around it changed shape (a blocker clearing).
 */
function focusedField(): { id: string; start: number | null; end: number | null } | null {
  const el = document.activeElement;
  if (!(el instanceof HTMLInputElement) && !(el instanceof HTMLTextAreaElement)) return null;
  if (el.id === '') return null;
  return { id: el.id, start: el.selectionStart, end: el.selectionEnd };
}

function restore(saved: ReturnType<typeof focusedField>): void {
  if (saved === null) return;
  const el = document.getElementById(saved.id);
  if (!(el instanceof HTMLInputElement) && !(el instanceof HTMLTextAreaElement)) return;
  if (document.activeElement === el) return;
  el.focus();
  if (saved.start !== null && saved.end !== null) {
    try {
      el.setSelectionRange(saved.start, saved.end);
    } catch {
      /* `type="number"` and `type="search"` throw in some browsers. */
    }
  }
}

function paint(s: Readonly<State>): void {
  const focused = focusedField();
  for (const [slot, host] of hosts) {
    if (host.isConnected) paintSlot(slot, host, s);
  }
  restore(focused);
}

function paintSlot(slot: RatingSlot, host: HTMLElement, s: Readonly<State>): void {
  if (slot === 'rating-form') {
    render(host, rateInputs(s, inHand));
    return;
  }
  if (slot === 'rating-history') {
    render(host, structureRatings(s, inHand));
    return;
  }
  render(
    host,
    h(
      'div',
      { class: 'rt-main' },
      deploymentBanner(s),
      s.error === null
        ? null
        : h(
            'div',
            { class: 'rt-bnr rt-bnr--err' },
            h('div', null, h('strong', { text: 'That did not work' }), h('p', { text: s.error })),
            h('span', { class: 'spacer' }),
            h('button', {
              type: 'button',
              class: 'btn',
              text: 'Dismiss',
              onclick: () => actions.dismissError(),
            }),
          ),
      SCREENS[s.screen](s),
    ),
  );
}

/**
 * Reconcile Bridge Rating with the slot the main render just drew, or did not.
 * Called after every main paint, like the other things that own a subtree.
 */
export function syncRating(selectedId?: string, active = false): void {
  /* THE STRUCTURE IN HAND IS ONE STRUCTURE. The page's explorer and property
     panel show the main store's selection beside this app's screens, so a
     structure picked elsewhere — the title bar's search, another app — is the
     one this register lights. Only once started: a session that never opens
     Bridge Rating is not made to start it by a selection. */
  if (started && selectedId && store.get().selected !== selectedId) {
    lastSelected = selectedId;
    actions.select(selectedId);
  }
  const found = new Map<RatingSlot, HTMLElement>();
  for (const slot of SLOTS) {
    const el = document.querySelector<HTMLElement>(`[data-keep="${slot}"]`);
    if (el !== null) found.set(slot, el);
  }
  /* STARTED WHILE THE APP IS ON SCREEN, not only when a slot is: the explorer
     counts what is outstanding and what is on record for the structure in hand
     while the content panel is still showing the 3D model. */
  if (found.size === 0 && !active) {
    hosts.clear();
    return;
  }
  if (!started) {
    started = true;
    /* Which department, before the network has said — then ask the deployment
       what it is. Both were Bridge Rating's boot; they are its first mount now. */
    actions.tenantResolved();
    store.subscribe((next) => {
      if (pendingRate !== null && next.bridges !== null) {
        const id = pendingRate;
        pendingRate = null;
        if (next.draft.bridgeId === id) {
          actions.startRating(id);
          return;
        }
      }
      paint(next);
      for (const fn of watchers) fn();
      if (next.selected !== null && next.selected !== lastSelected) {
        lastSelected = next.selected;
        for (const fn of selectWatchers) fn(next.selected);
      }
    });
    void actions.connect();
    if (selectedId) {
      lastSelected = selectedId;
      actions.select(selectedId);
    }
  }
  const movedTo = selectedId ?? null;
  const moved = movedTo !== inHand;
  inHand = movedTo;
  const focused = focusedField();
  for (const slot of SLOTS) {
    const el = found.get(slot);
    if (el === undefined) {
      hosts.delete(slot);
      continue;
    }
    /* A new box, or the same box repurposed — the reconciler empties a slot
       whose name changed (see KEEP in src/core/dom.ts) — or the structure the
       property panel's pages are of has moved. */
    if (hosts.get(slot) !== el || el.childElementCount === 0 || (moved && slot !== 'rating')) {
      hosts.set(slot, el);
      paintSlot(slot, el, store.get());
    }
  }
  restore(focused);
}

/**
 * What the explorer prints beside Bridge Rating's entries for a structure: the
 * values still outstanding on a rating begun on it, and how many ratings of it
 * are on record. Null where the answer is not known yet — no draft on this
 * structure, or the ratings not read.
 */
export function ratingHints(bridgeId: string): { outstanding: number | null; onRecord: number | null } {
  const s = store.get();
  return {
    outstanding: s.draft.bridgeId === bridgeId ? outstandingFor(s.draft) : null,
    onRecord: s.ratings === null ? null : s.ratings.filter((r) => r.bridgeId === bridgeId).length,
  };
}

/**
 * Whether a structure is due to be rated again (LC-24), for the bridge
 * record: Bridge Rating's ratings over the register's own defects. Null until
 * the ratings have been read.
 */
export function ratingDueFor(b: Bridge, defects: readonly RegisterDefect[]): RerateStatus | null {
  /* TEMPORARY — the FWO demonstration's rating is known before Bridge Rating
     has started. Delete with src/data/fwo-demo.ts. */
  const ratings = store.get().ratings ?? (demoRatingsFor(active().id).length > 0 ? demoRatingsFor(active().id) : null);
  if (ratings === null) return null;
  return reratingOf(b, ratings, defects, active().rerating, new Date().toISOString().slice(0, 10));
}

/** Be told when Bridge Rating's own state changes — the ribbon draws its screen. */
export function onRatingChange(fn: () => void): void {
  watchers.add(fn);
}

/**
 * Be told when a structure is picked in Bridge Rating — a row of its register,
 * or the structure a rating was begun on — so the page's explorer, property
 * panel and 3D model can follow it.
 */
export function onRatingSelect(fn: (id: string) => void): void {
  selectWatchers.add(fn);
}

/** Be told when a rating is accepted, so the page's register can re-read the posting it set. */
export function onRatingAccepted(fn: (bridgeId: string) => void): void {
  acceptedWatchers.add(fn);
}

/** Bridge Rating's screen on show, and the way to change it, for the ribbon. */
export function ratingScreen(): Screen {
  return store.get().screen;
}

export function goRating(screen: Screen): void {
  actions.go(screen);
}

/** The rating the Ratings screen has open, for the address (LC-31); null for the governing one. */
export function ratingOpened(): string | null {
  return store.get().opened;
}

/**
 * Put Bridge Rating where an address says (LC-31): its screen, and the rating
 * open on it. The rating form of a structure is that structure's draft — begun
 * here where the draft in hand is another's, as "Rate this structure" begins
 * one. The structure itself follows the page's selection (`syncRating`).
 */
export function followRatingAddress(screen: Screen, bridgeId: string, ratingId: string | null): void {
  const s = store.get();
  if (screen === 'rate' && bridgeId !== '' && s.draft.bridgeId !== bridgeId) {
    rateStructure(bridgeId);
    return;
  }
  if (s.screen !== screen || s.opened !== ratingId) store.set({ screen, opened: ratingId, error: null });
}

/**
 * The ratings of one structure as far as this page knows them, or null where
 * Bridge Rating has not read them — for the bridge dashboard (LC-30).
 */
export function ratingsOf(bridgeId: string): RatingRecord[] | null {
  /* TEMPORARY — as `ratingDueFor`. Delete with src/data/fwo-demo.ts. */
  const ratings = store.get().ratings ?? (demoRatingsFor(active().id).length > 0 ? demoRatingsFor(active().id) : null);
  return ratings === null ? null : ratings.filter((r) => r.bridgeId === bridgeId);
}

/**
 * Rate the structure in hand — the one the page's explorer and 3D model show.
 * A draft already begun on it is picked up where it was left; any other
 * structure starts a fresh one.
 */
export function rateStructure(bridgeId: string): void {
  if (store.get().draft.bridgeId === bridgeId) {
    actions.go('rate');
    return;
  }
  actions.startRating(bridgeId);
  /* Begun before Bridge Rating has read the register — from the 3D model, on
     its first visit — the draft has none of the register's values in it. It is
     begun again when the register arrives; see the subscription above. */
  if (store.get().bridges === null) pendingRate = bridgeId;
}
